// @flow
import supportedLanguages from './supportedLanguages'

// Types
export type LanguageDescriptor = {|
  name: string,
  value: string,
  intl: string,
  moment?: string,
  code: string,
  alias?: string,
  zendeskLocale: string,
  rtl?: boolean,
|}

type LanguagesSet = { [k: string]: LanguageDescriptor }

/**
 * @param  {string} name     Name of the language
 * @param  {string} intl     Name of the locale for Intl
 * @param  {string} moment   Name of the locale for moment.js
 * @param  {string} alias    If defined then this locale will be used
 *                           to load actual messages instead of the original
 * @param {string} zendeskLocale Locale code for zendesk widget
 * @param  {bool} rtl        Is the language right-to-left
 * @return {Object}
 */
export const LANGUAGES: LanguagesSet = supportedLanguages

const CORE_LANGS: LanguagesSet = Object.entries(LANGUAGES).reduce(
  (acc, [langName, langObj]) => {
    const coreLang = langName.toLowerCase().split('-')[0]
    if (!coreLang || acc[coreLang]) return acc
    return { ...acc, [coreLang]: langObj }
  },
  {}
)

/**
 * This function helps to find a language object by a full lang name or by
 * only a core lang name. It can resolve:
 * 1. Full lang name, like `en-us`
 * 2. If second part of the lang is not available, it will fallback
 *    to the core one, like `en-wooo` will be resolbed to `en-us`
 * 3. Only core lang name like `en` will be resolbed to `en-us`
 * @param  {string} langName
 * @return {LanguageDescriptor}
 */
export const findLanguage = (
  langName: string,
  defaultLang: string = 'en'
): LanguageDescriptor => {
  const [coreLang = 'en', subLang = ''] = langName
    .toLowerCase()
    .replace(/[^a-z]+/gi, '-')
    .split('-')

  const defaultLangObj =
    LANGUAGES[defaultLang] || CORE_LANGS[defaultLang] || CORE_LANGS.en

  const fullName =
    `${coreLang || ''}${subLang ? `-${subLang}` : ''}` || defaultLangObj.value

  return LANGUAGES[fullName] || CORE_LANGS[coreLang] || defaultLangObj
}

export default LANGUAGES
